import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Search, Plus, UserPlus, Edit, Mail, Phone, MapPin, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { deleteEmployee, getEmployees, getRoles, registerEmployee, updateEmployee } from '../../services/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { RolesPanel } from '../../components/dashboard/RolesPanel';

export const EmployeesPage = () => {
  const { isAdmin, can } = useAuth();
  const canCreate = can('employees.create');
  const canEdit = can('employees.edit');
  const canDelete = can('employees.delete');
  const [activeTab, setActiveTab] = useState('employees');
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    phone: '',
    address: '',
    hourly_rate: 0,
    vacation_days: 10
  });

  useEffect(() => {
    fetchEmployees();
    getRoles()
      .then((response) => setRoles(Array.isArray(response.data) ? response.data : []))
      .catch(() => setRoles([]));
  }, [activeTab]);

  const roleName = (key) =>
    roles.find((role) => role.key === key)?.name || (key === 'admin' ? 'Administrateur' : key === 'employee' ? 'Technicien' : key);
  // Seul un administrateur attribue le rôle Administrateur.
  const assignableRoles = roles.filter((role) => isAdmin || role.key !== 'admin');

  const fetchEmployees = async () => {
    try {
      const response = await getEmployees();
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && selectedEmployee) {
        const updateData = { ...formData };
        delete updateData.password;
        if (formData.password) updateData.password = formData.password;
        await updateEmployee(selectedEmployee.id, updateData);
        toast.success('Employé mis à jour');
      } else {
        await registerEmployee(formData);
        toast.success('Employé créé');
      }
      setFormOpen(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    }
  };

  const handleDeleteEmployee = async () => {
    if (!employeeToDelete || deleting) return;

    setDeleting(true);
    try {
      await deleteEmployee(employeeToDelete.id);
      setEmployees(currentEmployees =>
        currentEmployees.filter(employee => employee.id !== employeeToDelete.id)
      );
      toast.success('Employé supprimé');
      setDeleteOpen(false);
      setEmployeeToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'employee',
      phone: '',
      address: '',
      hourly_rate: 0,
      vacation_days: 10
    });
    setEditMode(false);
    setSelectedEmployee(null);
  };

  const openEditForm = (employee) => {
    setFormData({
      email: employee.email,
      password: '',
      first_name: employee.first_name,
      last_name: employee.last_name,
      role: employee.role,
      phone: employee.phone || '',
      address: employee.address || '',
      hourly_rate: employee.hourly_rate || 0,
      vacation_days: employee.vacation_days || 10
    });
    setSelectedEmployee(employee);
    setEditMode(true);
    setFormOpen(true);
  };

  const filteredEmployees = employees.filter(emp => 
    emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">Employés</h1>
          <p className="text-slate-500">Gérez votre équipe</p>
        </div>
        {canCreate && activeTab === 'employees' && (
          <Button 
            onClick={() => {
              resetForm();
              setFormOpen(true);
            }}
            className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8]"
            data-testid="add-employee-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Ajouter un employé
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
      {isAdmin && (
        <TabsList className="h-auto gap-1 rounded-xl bg-white p-1 shadow-sm border border-slate-200">
          <TabsTrigger value="employees" className="rounded-lg px-4 py-2 text-slate-600 data-[state=active]:bg-[#00b4d8] data-[state=active]:text-white data-[state=active]:shadow-none" data-testid="tab-employees">
            Employés
          </TabsTrigger>
          <TabsTrigger value="roles" className="rounded-lg px-4 py-2 text-slate-600 data-[state=active]:bg-[#00b4d8] data-[state=active]:text-white data-[state=active]:shadow-none" data-testid="tab-roles">
            Rôles
          </TabsTrigger>
        </TabsList>
      )}

      <TabsContent value="employees" className="space-y-6 mt-6">
      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Rechercher par nom ou courriel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Employee Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEmployees.map((employee) => (
          <Card key={employee.id} className="card-hover" data-testid={`employee-card-${employee.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-[#1a3a5c] flex items-center justify-center text-white font-semibold text-lg">
                  {employee.first_name?.[0]}{employee.last_name?.[0]}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#1a3a5c]">
                    {employee.first_name} {employee.last_name}
                  </h3>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    employee.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {roleName(employee.role)}
                  </span>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <p className="text-sm text-slate-500 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {employee.email}
                </p>
                {employee.phone && (
                  <p className="text-sm text-slate-500 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {employee.phone}
                  </p>
                )}
                {employee.address && (
                  <p className="text-sm text-slate-500 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {employee.address}
                  </p>
                )}
              </div>

              {(canEdit || canDelete) && (
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <div className="text-sm">
                    {employee.vacation_days !== undefined && (<>
                    <span className="text-slate-500">Vacances:</span>
                    <span className="ml-1 font-medium">{employee.vacation_days || 0} jours</span>
                    </>)}
                  </div>
                  <div className="flex items-center gap-2">
                    {canEdit && (employee.role !== 'admin' || isAdmin) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditForm(employee)}
                      aria-label={`Modifier ${employee.first_name} ${employee.last_name}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    )}
                    {canDelete && (employee.role !== 'admin' || isAdmin) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEmployeeToDelete(employee);
                        setDeleteOpen(true);
                      }}
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      aria-label={`Supprimer ${employee.first_name} ${employee.last_name}`}
                      data-testid={`delete-employee-${employee.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredEmployees.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <p>Aucun employé trouvé</p>
        </div>
      )}
      </TabsContent>

      {isAdmin && (
        <TabsContent value="roles" className="mt-6">
          <RolesPanel />
        </TabsContent>
      )}
      </Tabs>

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (deleting) return;
          setDeleteOpen(open);
          if (!open) setEmployeeToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet employé?</AlertDialogTitle>
            <AlertDialogDescription>
              Le compte de {employeeToDelete?.first_name} {employeeToDelete?.last_name} sera supprimé.
              Cette personne ne pourra plus se connecter ni être assignée. Ses feuilles de temps
              et autres historiques seront conservés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                handleDeleteEmployee();
              }}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-employee"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Modifier l\'employé' : 'Ajouter un employé'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label>Courriel *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>{editMode ? 'Nouveau mot de passe (laisser vide pour conserver)' : 'Mot de passe *'}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editMode}
              />
            </div>
            <div>
              <Label>Rôle</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {assignableRoles.length === 0 && <SelectItem value="employee">Technicien</SelectItem>}
                  {assignableRoles.map((role) => (
                    <SelectItem key={role.key} value={role.key}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Adresse</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Taux horaire ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Jours de vacances</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.vacation_days}
                  onChange={(e) => setFormData({ ...formData, vacation_days: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#00b4d8] hover:bg-[#009bb8]">
                {editMode ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
